/**
 * The 90-day trial-end experience's analytics vocabulary: the `action` tokens
 * its three surfaces carry, the payload shapes their seven events report, and
 * the emit seams the app binds.
 *
 * THREE SURFACES, ONE FILE, ONE FLOW. The reminder popover hands off to the
 * recap modal, and the discreet card is the post-trial-end variant of the same
 * hand-off; they share the `is_low_usage` / team-context vocabulary that the
 * flow is segmented by, and separating them would re-declare it three times.
 *
 * A FOURTH SEAM WITH NO SCREEN OF ITS OWN is at the bottom: the contact-sales
 * CTA on the 90-day variant of the two upgrade modals. Those modals are not this
 * root's — `upgrade_to_clinician_modal` and `upgrade_to_practice_modal` belong to
 * the billing and clinician-modal surfaces — so its payload carries only the
 * label this domain computes and the origin binds its own `screen_name` and
 * `action`. Declaring those two screens here would be minting another domain's
 * vocabulary in this one.
 */
import type {
  TRIALS_AND_UPGRADES_SCREEN,
  TrialPlanContext,
  TrialTeamContext,
} from './trials-and-upgrades-analytics.types';

/** The `action` tokens the recap modal's three CTAs carry. */
export const NINETY_DAY_TRIAL_RECAP_ACTION = {
  upgrade: 'ninety_day_trial_recap_upgrade',
  contactSales: 'ninety_day_trial_recap_contact_sales',
  remindMeLater: 'ninety_day_trial_recap_remind_me_later',
} as const;

export type NinetyDayTrialRecapActionToken =
  (typeof NINETY_DAY_TRIAL_RECAP_ACTION)[keyof typeof NINETY_DAY_TRIAL_RECAP_ACTION];

/** The `action` tokens the ending-soon reminder popover's two CTAs carry. */
export const NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION = {
  seeSummary: 'ninety_day_trial_ending_reminder_see_summary',
  close: 'ninety_day_trial_ending_reminder_close',
} as const;

export type NinetyDayTrialEndingReminderActionToken =
  (typeof NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION)[keyof typeof NINETY_DAY_TRIAL_ENDING_REMINDER_ACTION];

/** The `action` tokens the post-trial-end discreet card's two CTAs carry. */
export const NINETY_DAY_TRIAL_END_DISCREET_ACTION = {
  learnMore: 'ninety_day_trial_end_discreet_learn_more',
  close: 'ninety_day_trial_end_discreet_close',
} as const;

export type NinetyDayTrialEndDiscreetActionToken =
  (typeof NINETY_DAY_TRIAL_END_DISCREET_ACTION)[keyof typeof NINETY_DAY_TRIAL_END_DISCREET_ACTION];

/**
 * `modal_viewed` on the recap modal.
 *
 * Team context but NO `current_plan`, which is preserved rather than tidied: the
 * view has never carried the tier and the modal's clicks always have, so adding
 * it here would change what an existing row means.
 */
export type NinetyDayTrialRecapViewedProps = TrialTeamContext & {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['ninetyDayTrialRecap'];
};

/** `button_clicked` on any of the recap modal's three CTAs. */
export type NinetyDayTrialRecapClickProps = TrialPlanContext & {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['ninetyDayTrialRecap'];
  action: NinetyDayTrialRecapActionToken;
  label: string;
};

/**
 * `modal_viewed` on the ending-soon reminder popover.
 *
 * `is_low_usage` is the flow's segmentation key — it decides the popover's copy
 * and its CTA label — so it is required rather than optional, and it is read at
 * open time on the domain side.
 */
export type NinetyDayTrialEndingReminderViewedProps = TrialPlanContext &
  TrialTeamContext & {
    screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['ninetyDayTrialEndingSoonReminder'];
    is_low_usage: boolean;
  };

/**
 * `modal_dismissed` — the reminder popover timed out without engagement.
 *
 * The only `modal_dismissed` in this root, and the only event here whose context
 * is a SNAPSHOT: the two-hour timer outlives the resolution of tier and team
 * queries, so the domain side captures them at open time and replays them.
 * `auto_dismissed` is `true` rather than `boolean` because a user-driven dismiss
 * reports `button_clicked` through the close CTA instead — there is no path to
 * this payload with `false`.
 */
export type NinetyDayTrialEndingReminderDismissedProps = TrialPlanContext &
  TrialTeamContext & {
    screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['ninetyDayTrialEndingSoonReminder'];
    auto_dismissed: true;
    dismiss_after_ms: number;
    is_low_usage: boolean;
  };

/** `button_clicked` on the reminder popover's "See recap" or its dismiss. */
export type NinetyDayTrialEndingReminderClickProps = TrialPlanContext & {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['ninetyDayTrialEndingSoonReminder'];
  action: NinetyDayTrialEndingReminderActionToken;
  label: string;
};

/** `modal_viewed` on the post-trial-end discreet card. */
export type NinetyDayTrialEndDiscreetViewedProps = {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['ninetyDayTrialEndDiscreet'];
};

/** `button_clicked` on the discreet card's "Learn more" or its dismiss. */
export type NinetyDayTrialEndDiscreetClickProps = TrialPlanContext & {
  screen_name: (typeof TRIALS_AND_UPGRADES_SCREEN)['ninetyDayTrialEndDiscreet'];
  action: NinetyDayTrialEndDiscreetActionToken;
  label: string;
};

/**
 * `button_clicked` on the contact-sales CTA of the 90-day upgrade-modal variant.
 *
 * NO `screen_name` AND NO `action`, and that is the measurement rather than an
 * omission. This CTA is rendered inside two modals that belong to other roots
 * (`useClinicianUpgradeModal`, `usePracticeUpgradeModal`), each reporting its own
 * screen and its own action; the shared hook only knows the plan-name copy it
 * formats. So the origin binds both, and what crosses the seam is the one value
 * this domain owns.
 */
export type NinetyDayTrialContactSalesClickProps = {
  label: string;
};

/** The emit seam for the recap modal becoming visible. */
export type TrackNinetyDayTrialRecapViewed = (
  props: NinetyDayTrialRecapViewedProps
) => void;

/** The emit seam for a recap-modal CTA. */
export type TrackNinetyDayTrialRecapClick = (
  props: NinetyDayTrialRecapClickProps
) => void;

/** The emit seam for the reminder popover becoming visible. */
export type TrackNinetyDayTrialEndingReminderViewed = (
  props: NinetyDayTrialEndingReminderViewedProps
) => void;

/**
 * The emit seam for the reminder popover timing out.
 *
 * A separate seam from the viewed one rather than a shared alias, because the
 * two are different events — `modal_dismissed` against `modal_viewed` — even
 * though their payloads all but coincide. A host that bound one to the other is
 * a bug a shared type would hide.
 */
export type TrackNinetyDayTrialEndingReminderDismissed = (
  props: NinetyDayTrialEndingReminderDismissedProps
) => void;

/** The emit seam for a reminder-popover CTA. */
export type TrackNinetyDayTrialEndingReminderClick = (
  props: NinetyDayTrialEndingReminderClickProps
) => void;

/** The emit seam for the discreet card becoming visible. */
export type TrackNinetyDayTrialEndDiscreetViewed = (
  props: NinetyDayTrialEndDiscreetViewedProps
) => void;

/** The emit seam for a discreet-card CTA. */
export type TrackNinetyDayTrialEndDiscreetClick = (
  props: NinetyDayTrialEndDiscreetClickProps
) => void;

/** The emit seam for the 90-day variant's contact-sales CTA. */
export type TrackNinetyDayTrialContactSalesClick = (
  props: NinetyDayTrialContactSalesClickProps
) => void;

/**
 * The recap modal's two seams as one app-supplied bundle.
 *
 * Held as independently stable functions rather than read off this object:
 * `trackViewed` is a dependency of the open effect, so a port rebuilt each
 * render would re-fire it.
 */
export type NinetyDayTrialRecapAnalyticsPorts = {
  trackViewed: TrackNinetyDayTrialRecapViewed;
  trackClick: TrackNinetyDayTrialRecapClick;
};

/** The reminder popover's three seams as one app-supplied bundle. */
export type NinetyDayTrialEndingReminderAnalyticsPorts = {
  trackViewed: TrackNinetyDayTrialEndingReminderViewed;
  trackDismissed: TrackNinetyDayTrialEndingReminderDismissed;
  trackClick: TrackNinetyDayTrialEndingReminderClick;
};

/** The discreet card's two seams as one app-supplied bundle. */
export type NinetyDayTrialEndDiscreetAnalyticsPorts = {
  trackViewed: TrackNinetyDayTrialEndDiscreetViewed;
  trackClick: TrackNinetyDayTrialEndDiscreetClick;
};
